import { Api } from "@penn/web-common";
import { unwrap } from "@penn/web-common";

// =============================================================================
// Quotation / RFQ task lifecycle (svc-06-quotation, via gateway /v1/tasks).
// Mirrors packages/shared-types/src/svc-06-quotation/v1/{task,quote-finalize,
// state-history}.schema.ts. Types are kept inline (same convention as
// api/attachments.ts) so the web app doesn't depend on the backend package.
// =============================================================================

export type TaskFlowType = "engineer" | "sales";
export type MachineCostMode = "shared" | "dedicated";

export interface TaskSummary {
  taskId: string;
  status: string | null;
  flowType: TaskFlowType | null;
  machineCostMode: MachineCostMode | null;
  // Machine type of the frozen solution (svc-06 reads it from the task snapshot).
  // This is the product CATEGORY (e.g. "Servo-Electric"); null until a snapshot
  // exists. The Machine-type column now shows the specific model below instead.
  solutionMachineType: string | null;
  // The specific machine model the RFQ was frozen against — display name (e.g.
  // "824 MSPe", "A240-Bi") + product id/code. Shown in the Machine-type column
  // (POC parity: POC's "machine type" is the machine key/model, not the category).
  solutionName: string | null;
  solutionProductId: string | null;
  comment: string | null;
  createdBy: string | null;
  workingForUserId: string | null;
  workingForEmail: string | null;
  // Names on the summary so "Requested by" shows the user name.
  workingForFirstName: string | null;
  workingForLastName: string | null;
  requestedByEmail: string | null;
  requestedByFirstName: string | null;
  requestedByLastName: string | null;
  requestedByCompanyName: string | null;
  quotedAt: string | null;
  workflowEngReviewStatus: string | null;
  workflowQuoteStatus: string | null;
  hasSnapshot: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SupportResponseComment {
  response: string;
  respondedAt: string;
  responderName: string | null;
  responderEmail: string | null;
}

export interface TaskDetail extends TaskSummary {
  workingForCompanyName: string | null;
  requestedByPhone: string | null;
  requestedByCompanyAddress: string | null;
  requestedByRole: string | null;
  requestedByDepartment: string | null;
  requestedByRegion: string | null;
  quotedByEmail: string | null;
  quotedByName: string | null;
  /** CR-005: staff response thread on a message-only support task. */
  supportResponses: SupportResponseComment[];
}

export interface PaginatedTasks {
  items: TaskSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface ListTasksQuery {
  status?: string;
  /**
   * Open vs quoted filter, derived server-side from `quoted_at`
   * ("false" = open/not-yet-quoted, "true" = quoted). This is DISTINCT from the
   * `status` column, which stays "draft" for a task's whole MVP lifecycle — so
   * `status:"open"` matches nothing. Callers that mean "is this task still open"
   * MUST use `quoted:"false"`, not `status`.
   */
  quoted?: "true" | "false";
  /** Current ADR-0027 responsibility inbox membership. */
  assigned?: "true" | "false";
  flowType?: TaskFlowType;
  createdByEmail?: string;
  limit?: number;
  offset?: number;
}

export interface CreateTaskInput {
  flowType?: TaskFlowType;
  comment?: string | null;
  machineCostMode?: MachineCostMode | null;
  workingForUserId?: string | null;
  workingForEmail?: string | null;
  workingForFirstName?: string | null;
  workingForLastName?: string | null;
  workingForCompanyName?: string | null;
  requestedByEmail?: string | null;
  requestedByFirstName?: string | null;
  requestedByLastName?: string | null;
  requestedByPhone?: string | null;
  requestedByCompanyName?: string | null;
  requestedByCompanyAddress?: string | null;
  requestedByRole?: string | null;
}

export type UpdateTaskInput = Partial<CreateTaskInput> & {
  status?: string | null;
};

// --- Snapshot (selection only; svc-10 owns cost columns) --------------------

export interface SnapshotFastenerInput {
  fastenerId: string;
  brand?: string | null;
  type?: string | null;
  qtyPerPart?: number | null;
  qtyPerPartA?: number | null;
  qtyPerPartB?: number | null;
}

export interface SnapshotApplicationInput {
  appId: string;
  name?: string | null;
  applicationNumber?: string | null;
  oem?: string | null;
  productionProgram?: string | null;
  material?: string | null;
  materialCode?: string | null;
  annualVolume?: number | null;
  batchSize?: number | null;
  collapsed?: boolean | null;
  savedBaselineAt?: string | null;
  savedId?: string | null;
  fasteners: SnapshotFastenerInput[];
}

export interface SnapshotSelectedOptionInput {
  partNo: string;
  description?: string | null;
  category?: string | null;
  toolingType?: string | null;
  machineType?: string | null;
  itemName?: string | null;
  partName?: string | null;
  price?: number | null;
  currency?: string | null;
  /** Free text typed against the `__CUSTOM_TOOLING__` placeholder line. */
  requirements?: string | null;
  /** Frozen configured quantity for this part. */
  qty?: number | null;
}

export interface TaskSnapshotInput {
  source?: string | null;
  createdAtClient?: string | null;
  solutionProductId: string;
  solutionName?: string | null;
  solutionMachineType?: string | null;
  regionCode: string;
  filterView?: string | null;
  filterSize?: string | null;
  filterPm?: boolean | null;
  filterRi?: boolean | null;
  filterForceMinKn?: number | null;
  filterForceMaxKn?: number | null;
  filterTypes?: string[];
  filterCompareIds?: string[];
  machineCostMode: MachineCostMode;
  applications: SnapshotApplicationInput[];
  selectedOptions?: SnapshotSelectedOptionInput[];
  selectedPartNos?: string[];
}

export interface TaskSnapshotSelectedOption {
  partNo: string;
  description: string | null;
  category: string | null;
  toolingType: string | null;
  machineType: string | null;
  itemName: string | null;
  partName: string | null;
  price: number | null;
  currency: string | null;
  requirements: string | null;
  /** Null only for snapshots created before quantity persistence shipped. */
  qty: number | null;
}

export interface TaskSnapshot {
  taskId: string;
  source: string | null;
  solutionProductId: string | null;
  solutionName: string | null;
  solutionMachineType: string | null;
  filterRegion: string | null;
  filterView: string | null;
  filterSize: string | null;
  filterPm: boolean | null;
  filterRi: boolean | null;
  filterForceMinKn: number | null;
  filterForceMaxKn: number | null;
  investmentCost: string | null;
  operatorCost: string | null;
  qualityCost: string | null;
  totalCost: string | null;
  costModelIdSnapshot: string | null;
  machineCostMode: MachineCostMode | null;
  selectedOptions: TaskSnapshotSelectedOption[];
  selectedPartNos: string[];
  filterTypes: string[];
  filterCompareIds: string[];
  costAvailable: boolean;
  costUnavailableReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ConfigureRequest {
  selectedOptions?: SnapshotSelectedOptionInput[];
  selectedPartNos?: string[];
}

/**
 * One frozen `task_snapshot_applications` row. `savedId` is the live application
 * UUID (null for ad-hoc apps never saved to the library) — used to re-fetch the
 * full ApplicationDetail when re-hydrating a task into the selector (D2).
 */
export interface TaskApplicationLink {
  appId: string;
  name: string | null;
  applicationNumber: string | null;
  oem: string | null;
  productionProgram: string | null;
  savedId: string | null;
  /** Enriched for the support-request applications view. */
  annualVolume?: string | null;
  batchSize?: string | null;
  /** "Brand Type" summaries, one per fastener. */
  fasteners?: string[];
}

/**
 * The selector's filter chips + comparison set, carried from the live workspace
 * into the RFQ snapshot at submit (capture) and read back on Open Task (restore)
 * — POC `filtersSnap`. `size` is the thread-size value
 * ("12") or null for "Any"; `forceMinKn/MaxKn` are the raw slider values.
 */
export interface RfqFilters {
  view?: string | null;
  types?: string[];
  size?: string | null;
  pm?: boolean | null;
  ri?: boolean | null;
  forceMinKn?: number | null;
  forceMaxKn?: number | null;
  compareIds?: string[];
}

// --- Finalize ---------------------------------------------------------------

export interface FinalizeQuoteRequest {
  note?: string | null;
  recipientEmail?: string | null;
}

export interface FinalizeQuoteResponse {
  taskId: string;
  quotedAt: string;
  quotedByEmail: string | null;
  quotedByName: string | null;
  workflowQuoteStatus: string | null;
  notificationOutboxId: string | null;
  notificationRecipient: string | null;
}

// --- State history ----------------------------------------------------------

export interface TaskStateHistoryEntry {
  id: string;
  taskId: string;
  flowType: TaskFlowType;
  eventType: string;
  fromStatus: string | null;
  toStatus: string;
  stepName: string | null;
  stepOrder: number | null;
  actorUserId: string | null;
  actorEmail: string;
  actorName: string | null;
  actorRoleCode: string | null;
  decisionReason: string | null;
  occurredAt: string;
}

export interface PaginatedTaskStateHistory {
  items: TaskStateHistoryEntry[];
  total: number;
  limit: number;
  offset: number;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

function toQuery(params: Record<string, unknown>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

/** GET /v1/tasks — internal region-scoped queue (sales/eng/admin). */
export async function listTasks(
  query: ListTasksQuery = {},
): Promise<PaginatedTasks> {
  const res = await Api.get(
    `/v1/tasks${toQuery(query as Record<string, unknown>)}`,
  );
  return unwrap<PaginatedTasks>(res.data);
}

/** GET /v1/tasks/mine — target-owned RFQs for the active self/Work-For identity. */
export async function listMyTasks(
  query: ListTasksQuery = {},
): Promise<PaginatedTasks> {
  const res = await Api.get(
    `/v1/tasks/mine${toQuery(query as Record<string, unknown>)}`,
  );
  return unwrap<PaginatedTasks>(res.data);
}

// Page size for the "fetch every row" helpers. The server caps a single page
// (svc-06 ListTasksQuerySchema max), so we loop offsets until the list is
// exhausted rather than requesting an unbounded limit in one query.
const FETCH_ALL_PAGE_SIZE = 1000;

async function fetchAllTaskPages(
  fetchPage: (q: ListTasksQuery) => Promise<PaginatedTasks>,
  query: ListTasksQuery,
): Promise<PaginatedTasks> {
  const items: TaskSummary[] = [];
  let offset = 0;
  let total = 0;
  // Guard against an unbounded loop if `total` never settles.
  for (let page = 0; page < 1000; page++) {
    const res = await fetchPage({
      ...query,
      limit: FETCH_ALL_PAGE_SIZE,
      offset,
    });
    items.push(...res.items);
    total = res.total;
    if (res.items.length < FETCH_ALL_PAGE_SIZE || items.length >= res.total)
      break;
    offset += FETCH_ALL_PAGE_SIZE;
  }
  return {
    items,
    total: total || items.length,
    limit: items.length,
    offset: 0,
  };
}

/** GET /v1/tasks (queue) — every matching row, paged through until exhausted. */
export async function listAllTasks(
  query: ListTasksQuery = {},
): Promise<PaginatedTasks> {
  return fetchAllTaskPages(listTasks, query);
}

/** GET /v1/tasks?assigned=true — every current responsibility-routed task. */
export async function listAllAssignedTasks(
  query: ListTasksQuery = {},
): Promise<PaginatedTasks> {
  return fetchAllTaskPages(listTasks, { ...query, assigned: "true" });
}

/** GET /v1/tasks/mine — every matching row, paged through until exhausted. */
export async function listAllMyTasks(
  query: ListTasksQuery = {},
): Promise<PaginatedTasks> {
  return fetchAllTaskPages(listMyTasks, query);
}

/** GET /v1/tasks/:taskId. */
export async function getTask(taskId: string): Promise<TaskDetail> {
  const res = await Api.get(`/v1/tasks/${encodeURIComponent(taskId)}`);
  return unwrap<TaskDetail>(res.data);
}

/** CR-005: message-only support close stamps the task Quoted (no "closed" status). */
export interface RespondAndCloseTaskResponse {
  taskId: string;
  status: "quoted";
  response: string;
  respondedAt: string;
  respondedBy: string;
  quotedAt: string;
}

/** POST /v1/tasks/:taskId/respond-and-close — records the staff response and
 * marks the message-only support task Quoted (CR-005). Staff-only server-side. */
export async function respondAndCloseTask(
  taskId: string,
  response: string,
): Promise<RespondAndCloseTaskResponse> {
  const res = await Api.post(
    `/v1/tasks/${encodeURIComponent(taskId)}/respond-and-close`,
    { response },
  );
  return unwrap<RespondAndCloseTaskResponse>(res.data);
}

/** POST /v1/tasks — create an RFQ task. */
export async function createTask(body: CreateTaskInput): Promise<TaskDetail> {
  const res = await Api.post(`/v1/tasks`, body);
  return unwrap<TaskDetail>(res.data);
}

/** PATCH /v1/tasks/:taskId. */
export async function updateTask(
  taskId: string,
  body: UpdateTaskInput,
): Promise<TaskDetail> {
  const res = await Api.patch(`/v1/tasks/${encodeURIComponent(taskId)}`, body);
  return unwrap<TaskDetail>(res.data);
}

/** DELETE /v1/tasks/:taskId. */
export async function deleteTask(taskId: string): Promise<void> {
  await Api.delete(`/v1/tasks/${encodeURIComponent(taskId)}`);
}

/** GET /v1/tasks/:taskId/snapshot — null when no snapshot yet. */
export async function getSnapshot(
  taskId: string,
): Promise<TaskSnapshot | null> {
  const res = await Api.get(
    `/v1/tasks/${encodeURIComponent(taskId)}/snapshot`,
    {
      validateStatus: (s) => (s >= 200 && s < 300) || s === 404,
    },
  );
  if (res.status === 404) return null;
  return unwrap<TaskSnapshot>(res.data);
}

/** GET /v1/tasks/:taskId/applications — the task's frozen application links (D2). */
export async function getTaskApplications(
  taskId: string,
): Promise<TaskApplicationLink[]> {
  const res = await Api.get(
    `/v1/tasks/${encodeURIComponent(taskId)}/applications`,
    { validateStatus: (s) => (s >= 200 && s < 300) || s === 404 },
  );
  if (res.status === 404) return [];
  return unwrap<{ items: TaskApplicationLink[] }>(res.data).items;
}

/**
 * POST /v1/tasks/:taskId/applications — freeze ONLY the applications (no machine
 * snapshot) for a message-only support request, so they show in the support
 * response document/modal without moving the task out of the support flow.
 */
export async function writeTaskApplications(
  taskId: string,
  applications: SnapshotApplicationInput[],
): Promise<TaskApplicationLink[]> {
  const res = await Api.post(
    `/v1/tasks/${encodeURIComponent(taskId)}/applications`,
    { applications },
  );
  return unwrap<{ items: TaskApplicationLink[] }>(res.data).items;
}

/** POST /v1/tasks/:taskId/snapshot — freeze the selection (runs the cost engine). */
export async function writeSnapshot(
  taskId: string,
  body: TaskSnapshotInput,
): Promise<TaskSnapshot> {
  const res = await Api.post(
    `/v1/tasks/${encodeURIComponent(taskId)}/snapshot`,
    body,
  );
  return unwrap<TaskSnapshot>(res.data);
}

/** POST /v1/tasks/:taskId/configure — toggle options and re-cost. */
export async function configureTask(
  taskId: string,
  body: ConfigureRequest,
): Promise<TaskSnapshot> {
  const res = await Api.post(
    `/v1/tasks/${encodeURIComponent(taskId)}/configure`,
    body,
  );
  return unwrap<TaskSnapshot>(res.data);
}

/** POST /v1/tasks/:taskId/quote — finalize (svc-07 gated: 409 awaiting-approval). */
export async function finalizeQuote(
  taskId: string,
  body: FinalizeQuoteRequest = {},
): Promise<FinalizeQuoteResponse> {
  const res = await Api.post(
    `/v1/tasks/${encodeURIComponent(taskId)}/quote`,
    body,
  );
  return unwrap<FinalizeQuoteResponse>(res.data);
}

/** GET /v1/tasks/:taskId/state-history. */
export async function getStateHistory(
  taskId: string,
  query: { limit?: number; offset?: number } = {},
): Promise<PaginatedTaskStateHistory> {
  const res = await Api.get(
    `/v1/tasks/${encodeURIComponent(taskId)}/state-history${toQuery(query as Record<string, unknown>)}`,
  );
  return unwrap<PaginatedTaskStateHistory>(res.data);
}
